import { ArrowLeft, BookOpen, CheckCircle2, ChevronLeft, ChevronRight, KeyRound, LogOut, RefreshCw, Search, Settings2, ShieldCheck, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import AdminBookPanel from "@/components/AdminBookPanel";
import "@/Admin.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function apiRequest(path, options = {}) {
  let response = await fetch(`${API}${path}`, { ...options, credentials: "include" });
  if (response.status === 401 && path !== "/admin/refresh") {
    const refresh = await fetch(`${API}/admin/refresh`, { method: "POST", credentials: "include" });
    if (refresh.ok) response = await fetch(`${API}${path}`, { ...options, credentials: "include" });
  }
  return response;
}

function formatError(detail) {
  if (!detail) return "Algo não funcionou. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || JSON.stringify(item)).join(" ");
  return String(detail);
}

function statusLabel(status) {
  const labels = { approved: "aprovado", pending: "pendente", created: "criado", rejected: "recusado", cancelled: "cancelado", preference_error: "erro" };
  return labels[status] || status;
}

function dateLabel(value) {
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

export default function AdminDashboard() {
  const [admin, setAdmin] = useState(null);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tokenInput, setTokenInput] = useState("");
  const [book, setBook] = useState(null);
  const [view, setView] = useState("orders");
  const [selectedPage, setSelectedPage] = useState(0);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [bookLoading, setBookLoading] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const loadDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const meResponse = await apiRequest("/admin/me");
      if (!meResponse.ok) {
        window.location.assign("/admin/login");
        return;
      }
      const [ordersResponse, settingsResponse] = await Promise.all([apiRequest("/admin/orders"), apiRequest("/admin/settings/mercadopago")]);
      const ordersData = await ordersResponse.json();
      const settingsData = await settingsResponse.json();
      if (!ordersResponse.ok) throw new Error(formatError(ordersData.detail));
      if (!settingsResponse.ok) throw new Error(formatError(settingsData.detail));
      setAdmin(await meResponse.json());
      setOrders(ordersData);
      setSettings(settingsData);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDashboard(); }, []);

  const openBook = async () => {
    setView("book");
    if (book) return;
    setBookLoading(true);
    try {
      const response = await apiRequest("/admin/book");
      const data = await response.json();
      if (!response.ok) throw new Error(formatError(data.detail));
      setBook(data);
    } catch (bookError) {
      setError(bookError.message);
    } finally {
      setBookLoading(false);
    }
  };

  const saveToken = async (event) => {
    event.preventDefault();
    if (tokenInput.trim().length < 10) {
      setError("Cole um Access Token válido para atualizar a configuração.");
      return;
    }
    setSavingToken(true);
    setError("");
    setNotice("");
    try {
      const response = await apiRequest("/admin/settings/mercadopago", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ access_token: tokenInput.trim() }) });
      const data = await response.json();
      if (!response.ok) throw new Error(formatError(data.detail));
      setSettings(data);
      setTokenInput("");
      setNotice("Access Token salvo de forma protegida e mascarada.");
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingToken(false);
    }
  };

  const logout = async () => {
    await apiRequest("/admin/logout", { method: "POST" });
    window.location.assign("/admin/login");
  };

  const filteredOrders = useMemo(() => orders.filter((order) => {
    const matchesStatus = filter === "all" || order.status === filter;
    const haystack = `${order.buyer_name} ${order.buyer_email} ${order.payment_id || ""}`.toLowerCase();
    return matchesStatus && haystack.includes(query.toLowerCase());
  }), [orders, filter, query]);

  const approvedCount = orders.filter((order) => order.status === "approved").length;
  const pendingCount = orders.filter((order) => order.status === "pending").length;

  if (loading) return <main className="admin-loading"><RefreshCw size={21} className="admin-spin" /><span>Preparando sua sala privada…</span></main>;

  return (
    <main className="admin-shell">
      <header className="admin-header"><div className="admin-header-inner"><a href="/" className="admin-brand" data-testid="admin-home-link"><span>30</span><strong>DIAS PARA A LIBERDADE<small>painel reservado</small></strong></a><div className="admin-header-user"><span><ShieldCheck size={14} /> {admin?.email}</span><button type="button" onClick={logout} data-testid="admin-logout-button"><LogOut size={15} /> sair</button></div></div></header>
      <div className="admin-body">
        <aside className="admin-sidebar"><p className="admin-eyebrow">OPERAÇÃO</p><button className={view === "orders" ? "is-active" : ""} type="button" onClick={() => setView("orders")} data-testid="admin-orders-nav"><Users size={17} /> compradores <span>{orders.length}</span></button><button className={view === "book" ? "is-active" : ""} type="button" onClick={openBook} data-testid="admin-book-nav"><BookOpen size={17} /> livro completo</button><button className={view === "settings" ? "is-active" : ""} type="button" onClick={() => setView("settings")} data-testid="admin-settings-nav"><Settings2 size={17} /> Mercado Pago</button><a href="/" data-testid="admin-public-site-link"><ArrowLeft size={16} /> voltar ao site</a></aside>
        <section className="admin-content">
          <div className="admin-title-row"><div><p className="admin-eyebrow">BOM DIA, {admin?.name?.toUpperCase()}</p><h1>{view === "orders" ? "Acompanhe quem chegou." : view === "book" ? "Leitura administrativa." : "Configuração protegida."}</h1></div><div className="admin-title-actions"><button type="button" onClick={loadDashboard} data-testid="admin-refresh-button"><RefreshCw size={15} /> atualizar</button></div></div>
          {error && <div className="admin-alert admin-alert-wide" role="alert" data-testid="admin-dashboard-error">{error}</div>}
          {notice && <div className="admin-notice" role="status" data-testid="admin-dashboard-notice"><CheckCircle2 size={16} />{notice}</div>}
          {view === "orders" && <>
            <div className="admin-metrics"><article><span><Users size={15} /> pedidos registrados</span><strong>{orders.length}</strong></article><article><span><CheckCircle2 size={15} /> pagamentos aprovados</span><strong>{approvedCount}</strong></article><article><span><KeyRound size={15} /> aguardando confirmação</span><strong>{pendingCount}</strong></article></div>
            <div className="admin-table-toolbar"><label className="admin-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por nome, e-mail ou pagamento" aria-label="Buscar compradores" data-testid="admin-orders-search" /></label><select value={filter} onChange={(event) => setFilter(event.target.value)} aria-label="Filtrar por status" data-testid="admin-orders-filter"><option value="all">todos os status</option><option value="approved">aprovado</option><option value="pending">pendente</option><option value="rejected">recusado</option></select></div>
            <div className="admin-table-wrap"><table className="admin-orders-table"><thead><tr><th>comprador</th><th>valor</th><th>status</th><th>data</th><th>ID pagamento</th><th>leitura</th></tr></thead><tbody>{filteredOrders.length === 0 ? <tr><td colSpan="6" className="admin-empty">Nenhum comprador encontrado com esse filtro.</td></tr> : filteredOrders.map((order) => <tr key={order.id} data-testid={`admin-order-row-${order.id}`}><td><strong>{order.buyer_name}</strong><small>{order.buyer_email}</small></td><td>R$ {order.amount.toFixed(2).replace(".", ",")}</td><td><span className={`admin-status admin-status-${order.status}`}><i />{statusLabel(order.status)}</span></td><td>{dateLabel(order.created_at)}</td><td className="admin-mono">{order.payment_id || "—"}</td><td><button type="button" className="admin-table-action" onClick={openBook} data-testid={`admin-open-order-book-${order.id}`}><BookOpen size={14} /> abrir</button></td></tr>)}</tbody></table></div>
          </>}
          {view === "settings" && <section className="admin-settings-card"><div className="settings-heading"><span className="admin-settings-icon"><KeyRound size={20} /></span><div><p className="admin-eyebrow">CREDENCIAL DE COBRANÇA</p><h2>Mercado Pago, sem segredo exposto.</h2></div></div><p className="admin-settings-copy">O Access Token é criptografado antes de ser guardado. O painel nunca mostra o valor completo e o navegador nunca recebe essa credencial.</p><div className="admin-config-status"><span className={settings?.configured ? "is-ready" : "is-missing"}><i /> {settings?.configured ? "Access Token configurado" : "Access Token ainda não configurado"}</span><strong>{settings?.masked_token || "••••••••"}</strong></div><form className="admin-token-form" onSubmit={saveToken}><label><span>Novo Access Token de produção</span><input type="password" value={tokenInput} onChange={(event) => setTokenInput(event.target.value)} placeholder="Cole o token sem salvar no navegador" autoComplete="new-password" data-testid="admin-payment-token-input" /></label><button className="admin-primary-button" type="submit" disabled={savingToken} data-testid="admin-payment-token-save">{savingToken ? "Criptografando…" : "Atualizar token"} <ShieldCheck size={16} /></button></form><div className="admin-config-details"><span><i className={settings?.webhook_configured ? "is-ready" : "is-missing"} /> webhook {settings?.webhook_configured ? "configurado" : "pendente nos secrets"}</span><span>retorno: {settings?.frontend_url || "não definido"}</span></div><div className="admin-warning"><ShieldCheck size={16} /><p>O checkout só ficará ativo quando Access Token, Secret Signature e URLs públicas estiverem configurados. Nunca cole esse token em mensagens, PDFs ou no frontend.</p></div></section>}
          {view === "book" && <AdminBookPanel book={book} loading={bookLoading} selectedPage={selectedPage} onSelectPage={setSelectedPage} />}
        </section>
      </div>
    </main>
  );
}
